import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowRight, Crown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const aud = (n) => `A$${Number(n || 0).toFixed(Number(n) % 1 ? 2 : 0)}`;
export const isFounderPack = (p) => !p.region || /founder/i.test(p.name || '');

const FounderCard = ({ p, i, dark }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.12 }}
    className={`rounded-3xl overflow-hidden flex flex-col sm:flex-row ${dark ? 'bg-[#2D1037] text-[#FAF7F2]' : 'bg-white border border-[#E6DAC5]'}`} data-testid={`founder-card-${p.slug}`}>
    <div className="sm:w-2/5 bg-[#3B164C] relative min-h-[260px]">
      <img src={p.cover_image_url || p.guides?.[0]?.cover_image_url} alt={p.name} className="absolute inset-0 w-full h-full object-contain p-5" data-testid={`founder-cover-${p.slug}`} />
    </div>
    <div className="sm:w-3/5 p-7 flex flex-col">
      <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-3"><Crown size={14} /> Founder&apos;s Collection</span>
      <h3 className={`font-display text-2xl md:text-3xl font-medium mb-1 ${dark ? '' : 'text-[#1E1622]'}`}>{p.name}</h3>
      <p className={`text-sm mb-5 ${dark ? 'text-[#FAF7F2]/60' : 'text-[#1E1622]/55'}`} data-testid={`founder-count-${p.slug}`}>{p.guide_count} town guides{p.includes_toolkit ? ' + offline toolkits' : ''}</p>
      <div className="flex flex-wrap gap-1.5 mb-6" data-testid={`founder-towns-${p.slug}`}>
        {(p.guides || []).map((g) => (
          <Link key={g.product_id} to={`/shop/guides/${g.slug}`} title={g.town}>
            <img src={g.cover_image_url} alt={g.town} className={`w-10 h-10 rounded-full object-cover border-2 transition-transform duration-200 hover:scale-110 ${dark ? 'border-[#C5A059]/70' : 'border-white shadow'}`} />
          </Link>
        ))}
      </div>
      <div className="mt-auto flex flex-wrap items-center justify-between gap-4">
        <p className="font-display text-2xl" data-testid={`founder-price-${p.slug}`}>
          <span className={dark ? '' : 'text-[#3B164C]'}>{aud(p.price_aud)}</span>
          {p.savings_aud > 0 && <span className="text-sm text-[#C5A059] font-sans font-semibold ml-2">save {aud(p.savings_aud)}</span>}
        </p>
        <Link to={`/shop/packs/${p.slug}`} data-testid={`founder-cta-${p.slug}`} className="btn-gold inline-flex items-center gap-2 text-sm">View the collection <ArrowRight size={16} /></Link>
      </div>
    </div>
  </motion.div>
);

export const FoundersCollections = ({ variant = 'shop' }) => {
  const [packs, setPacks] = useState([]);
  useEffect(() => { axios.get(`${API}/studio-packs`).then((r) => setPacks((r.data.packs || []).filter(isFounderPack))).catch(() => {}); }, []);
  if (!packs.length) return null;
  const home = variant === 'home';
  const grid = <div className="grid lg:grid-cols-2 gap-8">{packs.map((p, i) => <FounderCard key={p.id} p={p} i={i} dark={home} />)}</div>;
  const heading = (
    <div className={`mb-10 ${home ? '' : 'text-center'}`}>
      <span className={`eyebrow mb-4 ${home ? '' : 'justify-center'}`}><span className="w-10 h-px bg-[#C5A059]" /> Small Towns Series</span>
      <h2 className={`font-display text-4xl md:text-5xl font-medium ${home ? 'text-[#FAF7F2]' : 'text-[#1E1622]'}`}>The Founder&apos;s Collections</h2>
      <p className={`mt-3 max-w-2xl font-light ${home ? 'text-[#FAF7F2]/70' : 'text-[#1E1622]/60 mx-auto'}`}>Our personal selection of Italy&apos;s most rewarding small towns across several regions, bundled at our best price.</p>
    </div>
  );
  if (home) {
    return (
      <section className="section-padding bg-[#1E1622]" data-testid="home-founders-collections">
        <div className="container-custom">{heading}{grid}</div>
      </section>
    );
  }
  return (
    <section id="founders-collections" className="container-custom mt-20 scroll-mt-28" data-testid="shop-section-founders-collections">
      {heading}{grid}
    </section>
  );
};
